package com.tixforgigs.common.ui.components.menu

import com.tixforgigs.common.core.annotations.MissingTaramiIconKey
import com.tixforgigs.common.core.extensions.orElse
import com.tixforgigs.common.core.models.ui.MenuItem
import com.tixforgigs.common.core.util.AuthUtil.isUserLoggedIn
import com.tixforgigs.common.core.util.IdHelper.generateRandomId
import io.kvision.core.Container
import io.kvision.event.clickFlow
import io.kvision.html.div
import io.kvision.material.ExperimentalMaterialApi
import io.kvision.material.divider.divider
import io.kvision.material.icon.icon
import io.kvision.material.icon.leadingIcon
import io.kvision.material.icon.trailingIcon
import io.kvision.material.iconbutton.iconButton
import io.kvision.material.menu.*
import io.kvision.material.slot.supportingText
import io.kvision.state.bind
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach


/**
 * # Popover API support was added in Chrome 114 and Safari 17.
 *
 * * For browsers that do not support the Popover API,
 *   md-menu will fall back to using fixed-positioned menus.
 *
 * @see <a href="https://caniuse.com/mdn-api_htmlelement_popover">Caniuse Popover API</a>
 */
@OptIn(ExperimentalMaterialApi::class)
fun Container.menuList(
  items: List<MenuItem>,
  icon: String = "menu", //"more_vert"
) {
  val menuAnchor = generateRandomId(numbersAllowed = false)

  div(className = "xm-menu") {
    val menu = menu(
      anchor = menuAnchor,
      positioning = MenuPositioning.Popover
    ) {
      items.forEachIndexed { menuIndex, menuItem ->
        menuItem.subMenu?.let { subMenuItems ->
          submenu(className = "xm-submenu") {
            ////menuItem.classNames.orEmpty().joinToString(separator = " ")
            item = MdMenuItem(className = "xm-menu-item") {
              menuItem.config?.invoke(this)
              menuItem.leadingIcon?.let { leadingIcon(it) }
              @MissingTaramiIconKey
              trailingIcon(menuItem.trailingIcon.orElse { "arrow_right" })
              supportingText(menuItem.text)
            }
            menu = MdMenu(
              className = "xm-menu",
              positioning = MenuPositioning.Popover
            ) {
              subMenuItems.forEachIndexed { subMenuIndex, subMenuItem ->
                subMenuItem.subMenu?.let { subSubMenuItems ->
                  submenu(className = "xm-submenu") {
                    ////menuItem.classNames.orEmpty().joinToString(separator = " ")
                    item = MdMenuItem(className = "xm-menu-item") {
                      subMenuItem.config?.invoke(this)
                      subMenuItem.leadingIcon?.let { leadingIcon(it) }
                      @MissingTaramiIconKey
                      trailingIcon(subMenuItem.trailingIcon.orElse { "arrow_right" })
                      supportingText(subMenuItem.text)
                    }
                    menu = MdMenu(
                      className = "xm-menu",
                      positioning = MenuPositioning.Popover
                    ) {
                      subSubMenuItems.forEachIndexed { subSubMenuIndex, subSubMenuItem ->
                        menuItem(className = "xm-menu-item") {
                          subSubMenuItem.config?.invoke(this)
                          subSubMenuItem.leadingIcon?.let { leadingIcon(it) }
                          subSubMenuItem.trailingIcon?.let { trailingIcon(it) }.orElse { "arrow_right" }
                          supportingText(subSubMenuItem.text)
                        }.clickFlow.onEach {
                          subSubMenuItem.onClick?.invoke()
                        }.launchIn(MainScope())
                        if (subSubMenuIndex != subSubMenuItems.lastIndex) {
                          divider()
                        }
                      }
                    }
                  }
                }
                if (subMenuItem.subMenu == null) {
                  menuItem(className = "xm-menu-item") {
                    subMenuItem.config?.invoke(this)
                    subMenuItem.leadingIcon?.let { leadingIcon(it) }
                    subMenuItem.trailingIcon?.let { trailingIcon(it) }.orElse { "arrow_right" }
                    supportingText(subMenuItem.text)
                  }.clickFlow.onEach {
                    subMenuItem.onClick?.invoke()
                  }.launchIn(MainScope())
                }
                if (subMenuIndex != subMenuItems.lastIndex) {
                  divider()
                }
              }
            }
          }
        }
        if (menuItem.subMenu == null) {
          menuItem(className = "xm-menu-item") {
            menuItem.config?.invoke(this)
            menuItem.leadingIcon?.let { leadingIcon(it) }
            menuItem.trailingIcon?.let { trailingIcon(it) }
            supportingText(menuItem.text)
          }.clickFlow.onEach {
            menuItem.onClick?.invoke()
          }.launchIn(MainScope())
        }
        if (menuIndex != items.lastIndex) {
          divider()
        }
      }
    }

    iconButton(className = "xm-icon-button") {
      id = menuAnchor
      setAttribute("aria-label", "Account Menu")
      @MissingTaramiIconKey
      icon(icon).bind(isUserLoggedIn) { loggedIn ->
        name = if (loggedIn) {
          "account_circle"
        } else {
          "menu"
        }
      }
    }.clickFlow.onEach {
      if (menu.open) {
        menu.close()
      } else {
        menu.open()
      }
    }.launchIn(MainScope())
  }
}
